import { useState } from "react";
import eventsAPI from "../../../api/eventsAPI";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEvent } from "../../../context/EventContext";
import { storage } from "../../../firebase";
import Loader from "../../Loader/Loader";
import { v4 as uuidv4 } from "uuid";
import defaultProfilePic from "../../../assest/defaultProfile.png";

// Component to handle the speaker form and speaker form preview.
//  * Handles the "Add Speaker" button click by setting the isFormVisible state to true,
//  * which displays the speaker form.
//  * Handles the "Save" button click by calling the API to create a new speaker and
//  * updating the speakers state with the new speaker data.
//  * If the speaker is created successfully, it sets the speakerAdded state to true
//  * and shows a success message.
//  * If the speaker is not created successfully, it shows an error message.
//  * Handles the "Edit" button click by setting the isFormVisible state to true and
//  * setting the editIndex state to the index of the speaker to be edited.
//  * Handles the "Delete Image" button click by setting the selectedFile state to null.
//  * Handles the file selection event by setting the selectedFile state to the chosen file.
//  * Handles the file upload progress and displays a progress bar.
//  * Handles the "Next" button click by calling the onNext function passed as a prop.
//  * Handles the "Add" button click by calling the handleSpeakerAdd function.
const SpeakerForm = ({ onNext }) => {
  const {
    speakerFormData,
    handleSpeakerChange,
    setSpeakerFormData,
    setLoading,
    loading,
    speakers,
    setSpeakerAdded,
    speakerAdded,
    formData,
    setEditIndex,
    editIndex,
    setSpeakers,
    mainEventID,
  } = useEvent();

  const [isFormVisible, setIsFormVisible] = useState(false);

  //   Handles the "Add Speaker" button click by setting the isFormVisible state to true,
  //   * which displays the speaker form.
  const handleAddSpace = () => {
    setIsFormVisible(true);
  };

  //   Handles the "Edit" button click by setting the isFormVisible state to true and
  //   setting the editIndex state to the index of the speaker to be edited.
  const handleEdit = (index) => {
    setSpeakerFormData(speakers[index]);
    setIsFormVisible(true);
    setEditIndex(index);
  };

  //   Handles the "Add Speaker" button click by setting the speakerFormData state to
  //   * an empty object with mainEventID, setting the isFormVisible state to true, and
  //   * setting the editIndex state to null. This displays the speaker form.
  const handleSpeakerAdd = () => {
    setSpeakerFormData({
      speaker_name: "",
      speaker_type: "",
      image_url: "",
      company_name: "",
      social_links: "",
      mainEventID: mainEventID,
    });
    setIsFormVisible(true);
    setEditIndex(null);
  };

  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  //   Handles the file selection for the speaker image.
  const handleSpeakerFile = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  //   Handles the "Delete Image" button click by setting the selectedFile state variable to null, effectively deleting the image.
  const handleDeleteImage = () => {
    setSelectedFile(null);
  };

  //   Handles the "Save" button click in the speaker form.
  //   If an image is selected, it uploads the image to Firebase Storage and then sends the image URL in the request.
  //   If the request is successful, it shows a success message, sets the isFormVisible state to false, and sets the speakerAdded state to true.
  //   It also updates the speakers state with the newly created speaker.
  //   If the editIndex state is not null, it updates the speaker at that index in the speakers state.
  //   Otherwise, it adds the new speaker to the speakers state.
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedFile !== null) {
        const uniqueID = uuidv4(); // Generate a unique ID

        // upload image and get download url
        const imageRef = storage.ref(
          `${formData.mainEventID}/speakers/${uniqueID}`
        );
        // Reference for image storage
        const uploadTask = imageRef.put(selectedFile); // Upload the image
        // Handle upload progress (optional)
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            // Progress logic (e.g., show a progress bar)
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress.toFixed(2));
          },
          (error) => {
            console.error("Error uploading image:", error);
            setLoading(false); // Stop loading
          },
          async () => {
            // Wait for upload to complete
            const imageUrl = await imageRef.getDownloadURL();

            const updatedFormData = {
              ...speakerFormData,
              img_id: uniqueID,
              image_url: imageUrl,
            };

            // Call the API with updated formData
            const response = await eventsAPI.post(
              "/organizer/speaker",
              updatedFormData
            );
            if (response.status === 200) {
              toast.success("Speaker added successfully!");
              setLoading(false);
              setIsFormVisible(false);
              setSpeakerAdded(true);
              // After updating speakers
              setSpeakers([...speakers, response.data]);

              if (editIndex !== null) {
                const updatedSpeakers = speakers.map((speaker, index) =>
                  index === editIndex ? response.data : speaker
                );
                setSpeakers(updatedSpeakers);
              } else {
                setSpeakers([...speakers, response.data]);
              }
            }
          }
        );
      } else {
        const uniqueID = uuidv4(); // Generate a unique ID
        const updatedFormData = {
          ...speakerFormData,
          img_id: uniqueID,
          image_url: null,
        };

        const response = await eventsAPI.post(
          "/organizer/speaker",
          updatedFormData
        );
        if (response.status === 200) {
          toast.success("Speaker added successfully!");
          setLoading(false);
          setIsFormVisible(false);
          setSpeakerAdded(true);
          // After updating speakers
          setSpeakers([...speakers, response.data]);

          if (editIndex !== null) {
            const updatedSpeakers = speakers.map((speaker, index) =>
              index === editIndex ? response.data : speaker
            );
            setSpeakers(updatedSpeakers);
          } else {
            setSpeakers([...speakers, response.data]);
          }
        }
      }
    } catch (error) {
      console.log("Error creating speaker:", error);
      setLoading(false);
    }
  };

  //   * Handles the "Update" button click in the speaker form.
  //   * Sends a POST request to the server to update a speaker.
  //   * If successful, it shows a success message, sets the isFormVisible state to false, and updates the local state with the updated speaker data.
  //   * If the request fails, it shows an error message.
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/organizer/updateSpeaker",
        speakerFormData
      );

      if (response.status === 200) {
        toast.success("Speaker updated successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Update the local state with the updated location data
        const updatedSpeakers = speakers.map((speaker, index) =>
          index === editIndex ? response.data : speaker
        );
        setSpeakers(updatedSpeakers);
      }
    } catch (error) {
      console.log("Error updating Location:", error);
      setLoading(false);
    }
  };

  //   * Handles the deletion of a speaker by sending a DELETE request to the server.
  //   * If successful, it removes the speaker from the local state and shows a success message.
  //   * If the request fails, it shows an error message.
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(`/organizer/speaker/${id}`); // Pass the location's id

      if (response.status === 200) {
        toast.success("Speaker deleted successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Remove the deleted location from the local state
        const updatedSpeakers = speakers.filter(
          (speaker) => speaker._id !== id
        );
        setSpeakers(updatedSpeakers);
      }
    } catch (error) {
      console.log("Error deleting Speaker:", error);
      setLoading(false);
    }
  };

  return (
    <div className="speaker-section">
      {!isFormVisible && !speakerAdded && (
        <button className="add-space" onClick={handleAddSpace}>
          Add Speaker
        </button>
      )}
      {isFormVisible ? (
        <form onSubmit={handleSave}>
          <h2>Speaker Details</h2>
          <div className="speaker-details">
            <label htmlFor="speaker-name">Speaker Name</label>
            <input
              value={speakerFormData.speaker_name}
              onChange={handleSpeakerChange}
              id="speaker-name"
              name="speaker_name"
              type="text"
              placeholder="Enter the name of your speaker"
              //   required
            />
            <label htmlFor="speaker-type">Speaker Type</label>
            <select
              value={speakerFormData.speaker_type}
              onChange={handleSpeakerChange}
              id="speaker-type"
              name="speaker_type"
              //   required
            >
              <option value="select">Select</option>
              <option value="technical">Technical</option>
              <option value="non-technical">Non-Technical</option>
            </select>
            <div className="upload-container">
              <label htmlFor="speaker-photo">Speaker Image</label>
              <input
                onChange={handleSpeakerFile}
                id="speaker-photo"
                name="image_url"
                type="file"
                // required
              />
              {selectedFile ? (
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="Profile Pic"
                  style={{ height: "60px", width: "60px", objectFit: "cover" }}
                />
              ) : speakerFormData.image_url ? (
                <img
                  src={speakerFormData.image_url}
                  alt="Profile Pic"
                  style={{ height: "60px", width: "60px", objectFit: "cover" }}
                />
              ) : (
                <img
                  src={defaultProfilePic}
                  alt="Profile Pic"
                  style={{ height: "60px", width: "60px", objectFit: "cover" }}
                />
              )}
              <button
                type="button"
                className="add-space"
                onClick={handleDeleteImage}
              >
                Delete
              </button>
              {uploadProgress > 0 && (
                <div className="progress-bar">
                  <div
                    className="progress"
                    style={{
                      width: `${uploadProgress}%`,
                      backgroundColor: "green",
                      color: "white",
                    }}
                  >
                    {uploadProgress}%
                  </div>
                </div>
              )}
              <p className="upload-info">Valid file formats: JPG, GIF, PNG.</p>
            </div>
            <label htmlFor="company-name">Company Name</label>
            <input
              value={speakerFormData.company_name}
              onChange={handleSpeakerChange}
              id="company-name"
              name="company_name"
              type="text"
              placeholder="Enter the company name"
              //   required
            />
            <label htmlFor="social-links">Social Links (Optionals)</label>
            <input
              value={speakerFormData.social_links}
              onChange={handleSpeakerChange}
              id="social-link-1"
              name="social_links"
              type="text"
              placeholder="Enter the social link"
            />
            {/* Show the "Save" button if adding a new location, and "Update" button if editing */}
            {editIndex === null ? (
              <button
                type="submit"
                className="create-button"
                onClick={handleSave}
              >
                {loading ? <Loader /> : "SAVE"}
              </button>
            ) : (
              <>
                <button
                  type="submit"
                  className="create-button"
                  onClick={handleUpdate}
                >
                  {loading ? <Loader /> : "UPDATE"}
                </button>
                <button
                  type="submit"
                  className="create-button"
                  onClick={(e) => handleDelete(e, speakerFormData._id)}
                >
                  {loading ? <Loader /> : "DELETE"}
                </button>
              </>
            )}
          </div>
        </form>
      ) : (
        speakerAdded && (
          <>
            {speakers.map((speaker, index) => (
              <div className="saved-details" key={index}>
                <p>
                  <span>Speaker Name: </span> {speaker.speaker_name}
                </p>
                <img
                  src={speaker.image_url}
                  alt="speaker-img"
                  style={{ height: "50px", width: "50px" }}
                />
                <button className="add-space" onClick={() => handleEdit(index)}>
                  Edit
                </button>
              </div>
            ))}
            <div className="buttons-controller">
              <button className="add-space" onClick={onNext}>
                Next
              </button>
              <button
                className="add-space"
                onClick={(e) => handleSpeakerAdd(e, speakerFormData._id)}
              >
                Add
              </button>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default SpeakerForm;
